import { useEffect, useState } from "react";
import { Lock, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useCatalogs, catalogMap, useContactOptions } from "@/lib/lookups";
import { INVESTMENT_MOVES, LOAN_MOVES } from "@/lib/finance";
import { PAY_METHODS, SEMAPHORE } from "@/lib/conecta";

const TITLES = { contact: "Contacto", opportunity: "Negocio", commission: "Comisión", payment: "Pago recibido", investment_movement: "Movimiento de inversión / préstamo", need: "Necesidad" };
const CUR = { MXN: "MXN", USD: "USD", EUR: "EUR" };

const F = ({ label, children }) => <label className="block space-y-1"><span className="text-[11px] font-medium c-text-2">{label}</span>{children}</label>;
const Txt = ({ v, on, testid, type = "text" }) => <input data-testid={testid} type={type} className="c-input !py-2" value={v ?? ""} onChange={(e) => on(e.target.value === "" ? null : type === "number" ? Number(e.target.value) : e.target.value)} step="any" inputMode={type === "number" ? "decimal" : undefined} />;
const Sel = ({ v, on, options, testid, empty = "Sin definir" }) => (
  <select data-testid={testid} className="c-input !py-2" value={v ?? ""} onChange={(e) => on(e.target.value || null)}>
    <option value="">{empty}</option>
    {Object.entries(options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

const useList = (url, enabled) => {
  const [items, setItems] = useState([]);
  useEffect(() => { if (enabled) api.get(url).then((r) => setItems(r.data.items)).catch(() => {}); }, [url, enabled]);
  return items;
};

const ContactBody = ({ a, set, setData }) => {
  const all = useContactOptions(a.mode === "link");
  const d = a.data;
  return (
    <div className="space-y-2">
      <Sel testid={`action-${a.key}-mode`} v={a.mode === "link" ? a.contact_id || "__link" : "__new"} empty="Elige…"
        options={{ __new: `Crear nuevo: ${d.name}`, ...Object.fromEntries(a.candidates.map((c) => [c.id, `Usar existente: ${c.name}${c.company ? ` · ${c.company}` : ""}`])), __link: "Otro contacto existente…" }}
        on={(v) => set(v === "__new" ? { mode: "create", contact_id: null } : v === "__link" ? { mode: "link", contact_id: null } : { mode: "link", contact_id: v })} />
      {a.mode === "link" && !a.candidates.some((c) => c.id === a.contact_id) && (
        <Sel testid={`action-${a.key}-contact`} v={a.contact_id} on={(v) => set({ contact_id: v })} empty="Elige contacto" options={Object.fromEntries(all.map((c) => [c.id, c.name]))} />
      )}
      {a.mode === "create" && (
        <div className="grid grid-cols-2 gap-2">
          <F label="Nombre"><Txt testid={`action-${a.key}-name`} v={d.name} on={(v) => setData({ name: v })} /></F>
          <F label="Empresa"><Txt testid={`action-${a.key}-company`} v={d.company} on={(v) => setData({ company: v })} /></F>
          <F label="Cargo"><Txt v={d.position} on={(v) => setData({ position: v })} /></F>
          <F label="Teléfono"><Txt v={d.phone} on={(v) => setData({ phone: v })} /></F>
          <F label="Correo"><Txt v={d.email} on={(v) => setData({ email: v })} /></F>
        </div>
      )}
    </div>
  );
};

const OppBody = ({ a, set, setData, contactKeys, isOwner }) => {
  const cats = useCatalogs();
  const opps = useList("/opportunities", a.mode === "update");
  const d = a.data;
  return (
    <div className="space-y-2">
      <Sel testid={`action-${a.key}-mode`} v={a.mode} empty="Elige…" options={{ create: "Crear negocio nuevo", update: "Actualizar negocio existente" }} on={(v) => set({ mode: v || "create" })} />
      {a.mode === "update" && <Sel testid={`action-${a.key}-opportunity`} v={a.opportunity_id} empty="Elige negocio" options={Object.fromEntries(opps.map((o) => [o.id, o.title]))} on={(v) => set({ opportunity_id: v })} />}
      <div className="grid grid-cols-2 gap-2">
        {a.mode === "create" && <div className="col-span-2"><F label="Título"><Txt testid={`action-${a.key}-title`} v={d.title} on={(v) => setData({ title: v })} /></F></div>}
        <F label="Etapa"><Sel testid={`action-${a.key}-stage`} v={d.stage} options={catalogMap(cats, "stages")} on={(v) => setData({ stage: v })} /></F>
        <F label="Semáforo"><Sel v={d.semaphore} options={Object.fromEntries(Object.keys(SEMAPHORE).map((k) => [k, k]))} on={(v) => setData({ semaphore: v })} /></F>
        <F label="Quien pide"><Sel testid={`action-${a.key}-requester`} v={d.requester_key} options={contactKeys} on={(v) => setData({ requester_key: v })} /></F>
        <F label="Quien resuelve"><Sel testid={`action-${a.key}-resolver`} v={d.resolver_key} options={contactKeys} on={(v) => setData({ resolver_key: v })} /></F>
        <div className="col-span-2"><F label="Próximo paso"><Txt testid={`action-${a.key}-next-step`} v={d.next_step_text} on={(v) => setData({ next_step_text: v })} /></F></div>
        <F label="Fecha"><Txt testid={`action-${a.key}-next-date`} type="date" v={d.next_step_date} on={(v) => setData({ next_step_date: v })} /></F>
        <F label="Potencial"><Txt type="number" v={d.potential_amount} on={(v) => setData({ potential_amount: v })} /></F>
        {isOwner && <F label="Cobrado por quien resuelve"><Txt type="number" v={d.resolver_collected_amount} on={(v) => setData({ resolver_collected_amount: v })} /></F>}
        {isOwner && <F label="Moneda cobrado"><Sel v={d.resolver_collected_currency} options={CUR} on={(v) => setData({ resolver_collected_currency: v })} /></F>}
      </div>
    </div>
  );
};

const CommissionBody = ({ a, setData }) => {
  const cats = useCatalogs();
  const d = a.data;
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2"><F label="Modalidad"><Sel testid={`action-${a.key}-modality`} v={d.modality} options={catalogMap(cats, "commission_modalities")} on={(v) => setData({ modality: v })} /></F></div>
      <F label="%"><Txt testid={`action-${a.key}-percentage`} type="number" v={d.percentage} on={(v) => setData({ percentage: v })} /></F>
      <F label="Monto fijo"><Txt type="number" v={d.fixed_amount} on={(v) => setData({ fixed_amount: v })} /></F>
      <F label="Moneda"><Sel v={d.currency} options={CUR} on={(v) => setData({ currency: v })} /></F>
      <F label="Condición"><Txt v={d.notes} on={(v) => setData({ notes: v })} /></F>
      <p className="col-span-2 text-[11px] c-muted">Se registra como pactada verbal y sin confirmar por escrito.</p>
    </div>
  );
};

const PaymentBody = ({ a, set, setData, agreements }) => (
  <div className="grid grid-cols-2 gap-2">
    <div className="col-span-2"><F label="Comisión (por cobrar)"><Sel testid={`action-${a.key}-agreement`} v={a.agreement_id} empty={agreements.length ? "Elige comisión" : "No hay comisiones por cobrar en el negocio"} options={Object.fromEntries(agreements.map((g) => [g.id, g.label]))} on={(v) => set({ agreement_id: v })} /></F></div>
    <F label="Monto"><Txt type="number" v={a.data.amount} on={(v) => setData({ amount: v })} /></F>
    <F label="Moneda"><Sel v={a.data.currency} options={CUR} on={(v) => setData({ currency: v })} /></F>
    <F label="Fecha"><Txt type="date" v={a.data.date} on={(v) => setData({ date: v })} /></F>
    <F label="Forma"><Sel v={a.data.method} options={PAY_METHODS} on={(v) => setData({ method: v })} /></F>
  </div>
);

const InvestmentBody = ({ a, set, setData }) => {
  const loan = a.target_kind === "loan";
  const items = useList(loan ? "/loans" : "/investments", true);
  return (
    <div className="grid grid-cols-2 gap-2">
      <F label="Es"><Sel v={a.target_kind} empty="Elige" options={{ investment: "Inversión", loan: "Préstamo" }} on={(v) => set({ target_kind: v || "investment", target_id: null, data: { ...a.data, type: null } })} /></F>
      <F label={loan ? "Préstamo" : "Inversión"}><Sel testid={`action-${a.key}-target`} v={a.target_id} empty="Elige" options={Object.fromEntries(items.map((i) => [i.id, i.name || i.borrower_name]))} on={(v) => set({ target_id: v })} /></F>
      <F label="Movimiento"><Sel testid={`action-${a.key}-type`} v={a.data.type} options={loan ? LOAN_MOVES : INVESTMENT_MOVES} on={(v) => setData({ type: v })} /></F>
      <F label="Monto"><Txt type="number" v={a.data.amount} on={(v) => setData({ amount: v })} /></F>
      <F label="Moneda"><Sel v={a.data.currency} options={CUR} on={(v) => setData({ currency: v })} /></F>
      <F label="Fecha"><Txt type="date" v={a.data.date} on={(v) => setData({ date: v })} /></F>
    </div>
  );
};

const NeedBody = ({ a, setData, contactKeys }) => (
  <div className="grid grid-cols-2 gap-2">
    <div className="col-span-2"><F label="Necesidad"><Txt testid={`action-${a.key}-text`} v={a.data.text} on={(v) => setData({ text: v })} /></F></div>
    <F label="Especialidad"><Txt v={a.data.specialty} on={(v) => setData({ specialty: v })} /></F>
    <F label="Quién la tiene"><Sel v={a.data.contact_key} options={contactKeys} on={(v) => setData({ contact_key: v })} /></F>
  </div>
);

const BODIES = { contact: ContactBody, opportunity: OppBody, commission: CommissionBody, payment: PaymentBody, investment_movement: InvestmentBody, need: NeedBody };

export const ActionCard = ({ a, onChange, readOnly, result, error, ...ctx }) => {
  const Body = BODIES[a.type];
  const set = (patch) => onChange({ ...a, ...patch });
  const setData = (patch) => onChange({ ...a, data: { ...a.data, ...patch } });
  const disabled = readOnly || a.locked || !!result;
  return (
    <div data-testid={`action-card-${a.key}`} className={`c-card p-3.5 space-y-2.5 ${!a.enabled || a.locked ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold flex items-center gap-1.5">{TITLES[a.type]}
          {a.owner_only && <Lock className="w-3 h-3 c-accent" />}
          {a.amounts_hidden && <EyeOff className="w-3 h-3 c-muted" />}
        </p>
        <label className="flex items-center gap-2 text-xs c-text-2">
          {a.locked ? "Solo el dueño" : result ? "Aplicado" : "Incluir"}
          <input type="checkbox" data-testid={`action-${a.key}-toggle`} className="w-5 h-5 accent-[#bc9538]" disabled={disabled} checked={!!a.enabled && !a.locked} onChange={(e) => set({ enabled: e.target.checked })} />
        </label>
      </div>
      {result && <p data-testid={`action-${a.key}-result`} className="text-xs text-emerald-600 dark:text-emerald-400">{result.label}</p>}
      {error && <p data-testid={`action-${a.key}-error`} className="text-xs text-red-500">{error}</p>}
      {a.enabled && !disabled && <Body a={a} set={set} setData={setData} {...ctx} />}
    </div>
  );
};
